import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Table, Button, Modal, Form, Input, Select, Space, Tag, App } from 'antd';
import { PlusOutlined, MailOutlined, LockOutlined } from '@ant-design/icons';
import apiClient from '../api/client';
import { formatDateTime } from '../utils/datetime';

const { Title, Text } = Typography;

interface UserRow {
  id: string;
  email: string;
  role: string;
  createdAt: string;
}

// Red is reserved for failure states across the panel; a role is not a fault,
// so admin gets a non-alarming hue that still stands apart from the rest.
const roleColors: Record<string, string> = {
  admin: 'purple',
  operator: 'blue',
  viewer: 'default',
};

const UsersPage: React.FC = () => {
  const { message } = App.useApp();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm();

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/users');
      setUsers(data.data.users || []);
    } catch {
      // Endpoint may not return list yet — silently ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const createUser = async (values: { email: string; password: string; role: string }) => {
    try {
      await apiClient.post('/users', values);
      message.success('User created');
      setCreateOpen(false);
      form.resetFields();
      fetchUsers();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to create user');
    }
  };

  const columns = [
    {
      title: 'Email', dataIndex: 'email', key: 'email', ellipsis: true,
      // Identifiers never wrap mid-word — the full value lives in the tooltip.
      render: (v: string) => <Text ellipsis={{ tooltip: v }}>{v}</Text>,
    },
    {
      title: 'Role', dataIndex: 'role', key: 'role', width: 110,
      render: (role: string) => <Tag color={roleColors[role] || 'default'}>{role}</Tag>,
    },
    {
      title: 'Created', dataIndex: 'createdAt', key: 'createdAt', width: 190,
      render: (v: string) => <Text type="secondary" style={{ fontSize: 12 }}>{formatDateTime(v)}</Text>,
    },
  ];

  return (
    <>
      {/* Title, count and the only action share one 32px line — see the density
          contract: at most ~120px of chrome before the first row of data. */}
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Space size={8} align="baseline">
          <Title level={4} style={{ margin: 0 }}>Users</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>{users.length} total</Text>
        </Space>
        <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          Add User
        </Button>
      </Space>

      <Table
        dataSource={users}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={false}
      />

      <Modal
        title="Create User"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        footer={null}
      >
        <Form form={form} size="small" layout="vertical" onFinish={createUser} initialValues={{ role: 'viewer' }}>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input prefix={<MailOutlined />} placeholder="user@example.com" />
          </Form.Item>
          <Form.Item name="password" label="Password" rules={[{ required: true, min: 8 }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="Minimum 8 characters" />
          </Form.Item>
          <Form.Item name="role" label="Role" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="admin">Admin</Select.Option>
              <Select.Option value="operator">Operator</Select.Option>
              <Select.Option value="viewer">Viewer</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">Create</Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default UsersPage;
